import { CSSProperties, FC, useMemo } from 'react';
import Image, { ImageProps } from 'next/image';
import { getCDNStaticImageURL } from 'src/utils/cdnStaticSourcesHelper';
import {
  getEnvVariable,
  NEXT_PUBLIC_ENV,
  NEXT_PUBLIC_ENV_VARIANT
} from 'src/utils/envVariablesHelper';

interface Props extends ImageProps {
  src: string;
  // This prop defines if src should be used as is
  pureSrc?: boolean;
  width?: number | `${number}`;
  objectFit?: string;
  layout?: string;
}

const NextImage: FC<Props> = ({
  src,
  pureSrc,
  width,
  height,
  objectFit,
  fill,
  layout,
  ...props
}) => {
  const currentEnv = getEnvVariable(NEXT_PUBLIC_ENV);
  const imgSrcPath = pureSrc ? src : getCDNStaticImageURL(src, currentEnv);

  const isCDNUsed =
    currentEnv === NEXT_PUBLIC_ENV_VARIANT.STAGE ||
    currentEnv === NEXT_PUBLIC_ENV_VARIANT.PROD;

  const style: CSSProperties = useMemo(() => {
    // No size styles if fill is provided
    return fill
      ? ({
          objectFit,
          layout
        } as CSSProperties)
      : ({
          maxWidth: `100%`,
          minWidth: `${width}px`,
          width: `${width}px`,
          maxHeight: `100%`,
          minHeight: `${height}px`,
          height: `${height}px`,
          objectFit,
          layout
        } as CSSProperties);
  }, [fill, width, height, objectFit]);

  const imageProps = useMemo(() => {
    return isCDNUsed
      ? {
          src: imgSrcPath,
          // Pass loader for Stage and Prod only because local path is used in case of Dev env
          loader: () => imgSrcPath,
          // Unoptimized should be provided if there is no width property because existing CDN loader doesn't implement width
          // https://nextjs.org/docs/messages/next-image-missing-loader-width
          unoptimized: !width,
          style
        }
      : { src: imgSrcPath, style };
  }, [isCDNUsed, imgSrcPath, width, height]);

  return (
    <Image
      width={width}
      height={height}
      fill={fill}
      {...imageProps}
      {...props}
    />
  );
};

export default NextImage;
